import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BadgeIcon from '@mui/icons-material/Badge';
import { useVolunteer } from '../../contexts/VolunteerContext';
import { formatDate } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';
import ShiftCheckInActions from '../../components/volunteers/ShiftCheckInActions';

const VolunteerMyShiftsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    status,
    myShifts,
    loading,
    error,
    fetchStatus,
    fetchMyShifts,
    cancelShift,
  } = useVolunteer();

  const [tabIndex, setTabIndex] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.volunteerStatus === 'approved') {
      fetchMyShifts();
    }
  }, [status?.volunteerStatus, fetchMyShifts]);

  // Redirect if not approved
  useEffect(() => {
    if (status && status.volunteerStatus !== 'approved') {
      navigate(ROUTES.VOLUNTEERS);
    }
  }, [status, navigate]);

  // Use start of today (midnight) so shifts dated today are classified as "upcoming"
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const upcomingShifts = useMemo(
    () =>
      myShifts.filter(
        (r) =>
          r.status === 'confirmed' &&
          r.shift &&
          new Date(r.shift.date) >= todayStart
      ),
    [myShifts, todayStart]
  );

  const pastShifts = useMemo(
    () =>
      myShifts.filter(
        (r) =>
          r.shift &&
          (new Date(r.shift.date) < todayStart || r.status === 'cancelled')
      ),
    [myShifts, todayStart]
  );

  const handleCancelClick = (shiftId: string) => {
    setSelectedShiftId(shiftId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedShiftId) return;

    setIsProcessing(true);
    setActionError(null);

    try {
      await cancelShift(selectedShiftId);
      setSuccessMessage('Shift registration cancelled successfully');
      setShowSuccess(true);
      await fetchMyShifts();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to cancel registration');
    } finally {
      setIsProcessing(false);
      setCancelDialogOpen(false);
      setSelectedShiftId(null);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedShiftId(null);
  };

  if (loading && !myShifts.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderShiftCard = (registration: typeof myShifts[0], isUpcoming: boolean) => {
    if (!registration.shift) return null;

    const shift = registration.shift;
    const isPast = new Date(shift.date) < todayStart;
    const isCancelled = registration.status === 'cancelled';

    return (
      <Grid item xs={12} sm={6} lg={4} key={registration.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            opacity: isPast || isCancelled ? 0.7 : 1,
          }}
        >
          <Box
            sx={{
              height: 6,
              backgroundColor: shift.eventId ? '#9c27b0' : '#00b7ed',
            }}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {registration.checkedIn && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Checked In"
                    size="small"
                    color="success"
                  />
                )}
                {isCancelled && (
                  <Chip
                    icon={<CancelIcon />}
                    label="Cancelled"
                    size="small"
                    color="error"
                  />
                )}
                {isPast && !isCancelled && !registration.checkedIn && (
                  <Chip label="Past" size="small" color="default" />
                )}
              </Box>
            </Box>

            <Typography variant="h6" gutterBottom>
              {shift.title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(shift.date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {shift.startTime} - {shift.endTime}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {shift.location}
                </Typography>
              </Box>
            </Box>

            {registration.role && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BadgeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Role: {registration.role.name}
                </Typography>
              </Box>
            )}

            {registration.checkedIn && registration.checkInTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Check-in: {new Date(registration.checkInTime).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}

            {registration.checkOutTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Check-out: {new Date(registration.checkOutTime).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}

            {registration.totalHours != null && registration.totalHours > 0 && (
              <Chip
                label={`${registration.totalHours} hour${registration.totalHours !== 1 ? 's' : ''} logged`}
                size="small"
                sx={{
                  mt: 1,
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 500,
                }}
              />
            )}
          </CardContent>

          {isUpcoming && !isCancelled && (
            <Box sx={{ px: 2, pb: 1 }}>
              <ShiftCheckInActions
                registration={registration}
                onChanged={async (message) => {
                  setSuccessMessage(message);
                  setShowSuccess(true);
                  await fetchMyShifts();
                }}
              />
            </Box>
          )}

          <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(ROUTES.VOLUNTEER_SHIFT_DETAIL.replace(':id', shift.id))
              }
            >
              View Shift
            </Button>
            {isUpcoming && !isCancelled && !registration.checkedIn && (
              <Button
                color="error"
                size="small"
                onClick={() => handleCancelClick(shift.id)}
              >
                Cancel
              </Button>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        My Shifts
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and manage your volunteer shifts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          sx={{
            '& .MuiTab-root.Mui-selected': { color: '#00b7ed' },
            '& .MuiTabs-indicator': { backgroundColor: '#00b7ed' },
          }}
        >
          <Tab label={`Upcoming (${upcomingShifts.length})`} />
          <Tab label={`Past (${pastShifts.length})`} />
        </Tabs>
      </Box>

      {tabIndex === 0 && (
        <>
          {upcomingShifts.length === 0 ? (
            <Alert severity="info">
              You have no upcoming shifts.{' '}
              <Button
                size="small"
                onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
                sx={{ ml: 1 }}
              >
                Browse Available Shifts
              </Button>
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {upcomingShifts.map((reg) => renderShiftCard(reg, true))}
            </Grid>
          )}
        </>
      )}

      {tabIndex === 1 && (
        <>
          {pastShifts.length === 0 ? (
            <Alert severity="info">
              No past shifts to display.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {pastShifts.map((reg) => renderShiftCard(reg, false))}
            </Grid>
          )}
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose}>
        <DialogTitle>Cancel Shift Registration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your registration for this shift? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} disabled={isProcessing}>
            Keep Registration
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? 'Cancelling...' : 'Cancel Registration'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VolunteerMyShiftsPage;
